import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist";
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Icon } from "../components/Icon";
import { prefersReducedMotion } from "../lib/format";
import { S } from "../strings";
import { annotationModeNone, loadPdf, textLayerClass } from "./pdf";

/** US Letter, the shape we reserve before a page's real size is known. */
const LETTER = 792 / 612;
const MAX_PAGE_CSS = 820;
const PAD = 24;
const ZOOMS = [70, 85, 100, 115, 130, 145, 160, 175];
/** iOS Safari refuses canvases above ~16.7 million pixels. */
const MAX_CANVAS_PIXELS = 16_000_000;

export interface PageSize {
  w: number;
  h: number;
}

export interface DocViewerProps {
  /** Shown in the bar and used to label each page. */
  title: string;
  url: string;
  withCredentials: boolean;
  /** Expected page count, so the layout doesn't jump while the file loads (and for the placeholder). */
  pageCount?: number;
  pill?: string;
  downloadHref?: string;
  /** Selectable, screen-reader-readable text over each page. */
  textLayer?: boolean;
  /** When the file can't be shown: a demo note instead of an error with Retry. */
  demoPath?: string;
  /** Drawn over each page, positioned in percent of the page. `aspect` is height ÷ width. */
  overlay?: (page: number, aspect: number, placeholder: boolean) => ReactNode;
  /** Change it to scroll the element marked `data-focus="true"` into view. */
  focusKey?: string | number | null;
  /** Centre the focused element (the signature spot) rather than just bringing it into view. */
  focusCenter?: boolean;
  /** No zoom and download tools (the phone sheet). */
  bare?: boolean;
  className?: string;
  onReady?: () => void;
}

type Load = { status: "loading" } | { status: "ready"; doc: PDFDocumentProxy; sizes: PageSize[] } | { status: "failed" };

export function DocViewer(props: DocViewerProps) {
  const { title, url, withCredentials, pageCount = 1, pill, downloadHref, textLayer, demoPath, overlay, focusKey, focusCenter, bare, className, onReady } = props;
  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const [zoom, setZoom] = useState(100);
  const [bodyW, setBodyW] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);
  const readyRef = useRef(onReady);
  readyRef.current = onReady;

  useEffect(() => {
    let live = true;
    setLoad({ status: "loading" });
    (async () => {
      try {
        const doc = await loadPdf(url, withCredentials, !!demoPath);
        const sizes: PageSize[] = [];
        for (let i = 1; i <= doc.numPages; i++) {
          const vp = (await doc.getPage(i)).getViewport({ scale: 1 });
          sizes.push({ w: vp.width, h: vp.height });
        }
        if (!live) return;
        setLoad({ status: "ready", doc, sizes });
        readyRef.current?.();
      } catch {
        if (live) setLoad({ status: "failed" });
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, withCredentials, attempt]);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const measure = () => setBodyW(el.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Bring the focused box into view — inside the viewer only, never by scrolling the page under the person's fingers.
  useEffect(() => {
    if (focusKey === null || focusKey === undefined) return;
    const body = bodyRef.current;
    const t = setTimeout(() => {
      const el = body?.querySelector<HTMLElement>('[data-focus="true"]');
      if (body && el) scrollWithin(body, el, !!focusCenter);
    }, 60);
    return () => clearTimeout(t);
  }, [focusKey, focusCenter, load.status, bodyW]);

  const sizes: PageSize[] =
    load.status === "ready" ? load.sizes : Array.from({ length: Math.max(1, pageCount) }, () => ({ w: 612, h: 792 }));
  const fit = Math.max(200, Math.min((bodyW || 640) - PAD * 2, MAX_PAGE_CSS));
  const pageW = Math.round((fit * zoom) / 100);
  const zi = ZOOMS.indexOf(zoom);
  const n = sizes.length;

  return (
    <section className={`viewer ${className ?? ""}`} aria-label={title}>
      <div className={`viewer-bar ${bare ? "slim" : ""}`}>
        {!bare && (
          <>
            <Icon n="doc" size={16} />
            <span className="doc-name">{title}</span>
            {pill && <span className="chip ok live-pill">{pill}</span>}
          </>
        )}
        <span className="spacer" />
        {!bare && <span className="hide-phone faint">{S.viewer.pages(n)}</span>}
        <button type="button" className="icon-btn" aria-label={S.viewer.zoomOut} disabled={zi <= 0} onClick={() => setZoom(ZOOMS[Math.max(0, zi - 1)]!)}>
          <Icon n="minus" />
        </button>
        <button type="button" className={`mono zoom-pct ${bare ? "" : "hide-phone"}`} onClick={() => setZoom(100)} title="Fit to width">
          {zoom}%
        </button>
        <button type="button" className="icon-btn" aria-label={S.viewer.zoomIn} disabled={zi >= ZOOMS.length - 1} onClick={() => setZoom(ZOOMS[Math.min(ZOOMS.length - 1, zi + 1)]!)}>
          <Icon n="plus" />
        </button>
        {downloadHref && !bare && (
          <a className="icon-btn" href={downloadHref} download aria-label={S.viewer.download} target="_blank" rel="noreferrer">
            <Icon n="download" />
          </a>
        )}
      </div>
      <div className="viewer-body" ref={bodyRef} tabIndex={0} aria-label={title}>
        <div className="pages" style={{ width: pageW }}>
          {sizes.map((s, i) => {
            const aspect = s.h / s.w;
            const label = S.viewer.pageLabel(title, i + 1, n);
            if (load.status === "failed") {
              return (
                <div key={i} className="paper pdf-page placeholder" style={{ width: pageW, height: Math.round(pageW * aspect) }} role="group" aria-label={label}>
                  <div className="placeholder-in">
                    <Icon n="doc" size={34} />
                    <b>{demoPath ? S.viewer.placeholderTitle : S.viewer.failed}</b>
                    {demoPath ? (
                      <span>{S.viewer.placeholderDemo(demoPath)}</span>
                    ) : (
                      i === 0 && (
                        <button type="button" className="btn btn-quiet" onClick={() => setAttempt((a) => a + 1)}>
                          {S.common.retry}
                        </button>
                      )
                    )}
                    <span className="placeholder-no">{label}</span>
                  </div>
                  {overlay && <div className="ov-layer">{overlay(i + 1, aspect, true)}</div>}
                </div>
              );
            }
            return (
              <PdfPage
                key={i}
                doc={load.status === "ready" ? load.doc : null}
                index={i + 1}
                width={pageW}
                aspect={aspect}
                base={s.w}
                label={label}
                textLayer={!!textLayer}
                overlay={overlay ? overlay(i + 1, aspect, false) : null}
              />
            );
          })}
        </div>
      </div>
      <div className="sr" aria-live="polite">
        {load.status === "loading" ? S.viewer.loading : load.status === "failed" && !demoPath ? S.viewer.failed : ""}
      </div>
    </section>
  );
}

function scrollWithin(body: HTMLElement, el: HTMLElement, center: boolean) {
  if (body.scrollHeight <= body.clientHeight + 1 && body.scrollWidth <= body.clientWidth + 1) return;
  const b = body.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  const margin = 48;
  const top = r.top - b.top + body.scrollTop;
  const left = r.left - b.left + body.scrollLeft;
  let y: number | null = null;
  let x: number | null = null;
  if (center) {
    y = top - (body.clientHeight - r.height) / 2;
  } else if (r.top < b.top + margin) {
    y = top - margin;
  } else if (r.bottom > b.bottom - margin) {
    y = top + r.height - body.clientHeight + margin;
  }
  if (r.left < b.left || r.right > b.right) x = left - (body.clientWidth - r.width) / 2;
  const behavior: ScrollBehavior = prefersReducedMotion() ? "auto" : "smooth";
  if (y !== null || x !== null) {
    body.scrollTo({ ...(y !== null ? { top: Math.max(0, y) } : {}), ...(x !== null ? { left: Math.max(0, x) } : {}), behavior });
  }
}

interface PdfPageProps {
  doc: PDFDocumentProxy | null;
  index: number;
  width: number;
  aspect: number;
  /** The page's width in PDF points. */
  base: number;
  label: string;
  textLayer: boolean;
  overlay: ReactNode;
}

/** One page: drawn to a canvas at the screen's pixel ratio once it scrolls near the view. */
function PdfPage({ doc, index, width, aspect, base, label, textLayer, overlay }: PdfPageProps) {
  const wrap = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const text = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [drawn, setDrawn] = useState(false);
  const height = Math.round(width * aspect);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "400px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!doc || !visible || width <= 0) return;
    let cancelled = false;
    let task: RenderTask | null = null;
    let tl: { cancel(): void } | null = null;
    const timer = setTimeout(async () => {
      try {
        const page = await doc.getPage(index);
        const c = canvas.current;
        if (cancelled || !c) return;
        let ratio = window.devicePixelRatio || 1;
        const cssScale = width / base;
        const px = width * height * ratio * ratio;
        if (px > MAX_CANVAS_PIXELS) ratio = Math.sqrt(MAX_CANVAS_PIXELS / (width * height));
        const viewport = page.getViewport({ scale: cssScale * ratio });
        const next = document.createElement("canvas");
        next.width = Math.floor(viewport.width);
        next.height = Math.floor(viewport.height);
        task = page.render({ canvas: next, viewport, annotationMode: await annotationModeNone() });
        await task.promise;
        if (cancelled) return;
        // Swap in the finished drawing at once, so zooming never flashes a blank page.
        c.width = next.width;
        c.height = next.height;
        c.getContext("2d")?.drawImage(next, 0, 0);
        setDrawn(true);
        if (textLayer && text.current) {
          const TextLayer = await textLayerClass();
          if (cancelled || !text.current) return;
          text.current.replaceChildren();
          const layer = new TextLayer({ textContentSource: page.streamTextContent(), container: text.current, viewport: page.getViewport({ scale: cssScale }) });
          tl = layer;
          await layer.render();
        }
      } catch {
        /* a cancelled render, or a page that can't be drawn — the reserved paper stays */
      }
    }, drawn ? 140 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      task?.cancel();
      tl?.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, visible, width, index]);

  const style = {
    width,
    height,
    "--total-scale-factor": String(width / base),
    "--scale-round-x": "1px",
    "--scale-round-y": "1px",
  } as CSSProperties;

  return (
    <div ref={wrap} className={`paper pdf-page ${drawn ? "drawn" : ""}`} style={style} role="group" aria-label={label}>
      <canvas ref={canvas} className="page-canvas" aria-hidden="true" />
      {textLayer && <div ref={text} className="textLayer" />}
      {overlay && <div className="ov-layer">{overlay}</div>}
    </div>
  );
}
